import { Badge, Select, TagsInput } from "@mantine/core";
import { useEffect, useState } from "react";
import { defaultIssueTypes, type TaskOptions } from "../shared/types";
import { api } from "./api";

const typeNames: Record<string, string> = {
  task: "常规任务",
  bug: "缺陷",
  feature: "新功能",
  improvement: "改进",
  refactor: "重构",
  docs: "文档",
  chore: "维护",
};
const presetLabels = [
  "frontend",
  "backend",
  "UI",
  "documentation",
  "performance",
  "security",
  "testing",
];
const typeLabel = (name: string) =>
  typeNames[name.toLowerCase()]
    ? `${name} · ${typeNames[name.toLowerCase()]}`
    : name;
export function useTaskOptions(projectId: string) {
  const [options, setOptions] = useState<TaskOptions>({
    labels: presetLabels,
    issueTypes: defaultIssueTypes.map((name) => ({ name })),
    typeMode: "local",
  });
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    api<TaskOptions>(`/api/projects/${projectId}/task-options`, {
      signal: controller.signal,
    })
      .then((value) => {
        if (!controller.signal.aborted) setOptions(value);
      })
      .catch(() => {
        if (!controller.signal.aborted)
          setOptions({
            labels: presetLabels,
            issueTypes: [],
            typeMode: "unavailable",
            warning: "分类选项加载失败，请重新打开任务重试。",
          });
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [projectId]);
  return { options, loading };
}

export function TaskTypePicker({
  value,
  onChange,
  options,
  disabled = false,
  label = "任务类型",
  repository = false,
}: {
  value: string | null;
  onChange: (value: string | null) => void;
  options: TaskOptions;
  disabled?: boolean;
  label?: string;
  repository?: boolean;
}) {
  const names = [
    ...new Set([
      ...options.issueTypes.map((item) => item.name),
      ...(value ? [value] : []),
    ]),
  ];
  return (
    <Select
      className="ui-form-field ui-task-type"
      label={label}
      description={
        repository
          ? options.typeMode === "github"
            ? "与 GitHub Issue Type 双向同步。"
            : options.typeMode === "local"
              ? "此仓库未启用 GitHub 类型，分类保存在 TGFlow。"
              : "类型选项暂不可用，现有值会保留。"
          : undefined
      }
      data={names.map((name) => ({ value: name, label: typeLabel(name) }))}
      value={value}
      onChange={onChange}
      disabled={disabled || options.typeMode === "unavailable"}
      placeholder="无类型"
      searchable
      clearable
      nothingFoundMessage="没有匹配的任务类型"
      maxDropdownHeight={220}
      comboboxProps={{ withinPortal: false }}
      clearButtonProps={{
        "aria-label": `清空${label}`,
        "aria-hidden": false,
        tabIndex: 0,
      }}
    />
  );
}

export function LabelPicker({
  value,
  onChange,
  options,
  disabled = false,
  label = "标签",
}: {
  value: string[];
  onChange: (value: string[]) => void;
  options: string[];
  disabled?: boolean;
  label?: string;
}) {
  return (
    <TagsInput
      className="ui-form-field ui-labels"
      label={label}
      description="可选已有或预置标签，也可输入新标签，按回车或逗号确认；最多 10 个。"
      placeholder="选择或输入标签"
      value={value}
      onChange={onChange}
      data={[...new Set([...value, ...options])]}
      disabled={disabled}
      maxTags={10}
      maxLength={40}
      splitChars={[",", "，"]}
      clearable
      maxDropdownHeight={220}
      comboboxProps={{ withinPortal: false }}
      clearButtonProps={{
        "aria-label": `清空${label}`,
        "aria-hidden": false,
        tabIndex: 0,
      }}
    />
  );
}

export function TaskTypeBadge({ name }: { name: string | null | undefined }) {
  if (!name) return null;
  const color =
    name.toLowerCase() === "bug"
      ? "red"
      : name.toLowerCase() === "feature"
        ? "blue"
        : "yellow";
  return (
    <Badge
      component="span"
      className="ui-type-badge"
      color={color}
      variant="light"
      radius="sm"
      title={typeLabel(name)}
    >
      {name}
    </Badge>
  );
}
